"use client";

import Link from "next/link";
import { ArrowRight, Lock, Sparkles, Shield } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";

export interface CtaSectionProps {
  id?: string;
  eyebrow: string;
  title: string;
  intro: string;
  promises: string[];
  ctaPrimary: { label: string; href: string };
  ctaSecondary?: { label: string; href: string };
  ctaTertiary?: { label: string; href: string };
  trustBar: string[];
  accent: string;
  secondaryAccent?: string;
}

export function CtaSection({
  id = "contact",
  eyebrow,
  title,
  intro,
  promises,
  ctaPrimary,
  ctaSecondary,
  ctaTertiary,
  trustBar,
  accent,
  secondaryAccent,
}: CtaSectionProps) {
  const accents = [accent, "var(--color-amber)", "var(--color-purple)", "var(--color-cyan)", "var(--color-emerald)"] as const;
  return (
    <Section id={id} className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[600px] rounded-full opacity-25 blur-[160px] animate-pulse-glow"
          style={{ background: accent }}
        />
        {secondaryAccent && (
          <div
            className="absolute top-1/3 right-1/4 h-72 w-72 rounded-full opacity-20 blur-[120px] animate-pulse-glow"
            style={{ background: secondaryAccent, animationDelay: "2s" }}
          />
        )}
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny" style={{ color: accent }}>
              {eyebrow}
            </span>
            <h2 className="mt-3">{title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{intro}</p>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="mt-8 glass rounded-2xl p-6 max-w-3xl">
            <div className="flex items-start gap-3">
              <Lock className="h-4 w-4 text-[var(--color-emerald)] mt-0.5 shrink-0" />
              <div className="space-y-2">
                {promises.map((p) => (
                  <p key={p} className="text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{p}</p>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={200}>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              href={ctaPrimary.href}
              className="btn-primary-gradient h-12 px-7 inline-flex items-center gap-2 rounded-full font-semibold text-base shadow-2xl"
              style={{ boxShadow: `0 25px 50px -12px color-mix(in oklch, ${accent} 40%, transparent)` }}
            >
              {ctaPrimary.label} <ArrowRight className="h-4 w-4" />
            </Link>
            {ctaSecondary && (
              <Link
                href={ctaSecondary.href}
                className="btn-glass h-12 px-7 inline-flex items-center gap-2 rounded-full font-semibold text-base"
              >
                {ctaSecondary.label}
              </Link>
            )}
            {ctaTertiary && (
              <Link
                href={ctaTertiary.href}
                className="btn-glass h-12 px-7 inline-flex items-center gap-2 rounded-full font-semibold text-base"
              >
                {ctaTertiary.label}
              </Link>
            )}
          </div>
        </Reveal>

        {trustBar.length > 0 && (
          <Reveal delay={300}>
            <div className="mt-12 glass rounded-2xl p-5">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
                {trustBar.map((item, i) => {
                  const tAccent = accents[i % accents.length]!;
                  const Icon = i === 4 ? Shield : Sparkles;
                  return (
                    <div key={item} className="flex items-center gap-2">
                      <Icon className="h-4 w-4 shrink-0" style={{ color: tAccent }} />
                      <span className="text-xs font-semibold leading-tight">{item}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>
        )}
      </Container>
    </Section>
  );
}
